import React from "react";
import {
  Avatar,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ShareOutlined as ShareIcon,
  Verified as VerifiedIcon,
  OpenInNew as OpenIcon,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { colors } from "../../../../../theme";
import { CommonButton } from "../../../../Components/ui";
import {
  FIVE_SLOT_CONFIG,
  SYMBOLS,
  DUMMY_BET_CURRENCY,
} from "./fiveSlotConfig";

const CURRENCY = DUMMY_BET_CURRENCY;

const FiveSlotMachineCard = ({ lotteryData }) => {
  const history = useHistory();

  const reels = FIVE_SLOT_CONFIG.initialReels.map(
    (id) => SYMBOLS.find((symbol) => symbol.id === id) || SYMBOLS[0]
  );

  const handleOpenLottery = () => {
    history.push(`/five-slotlottery/${lotteryData.lotteryId}`);
  };

  const handleShare = (event) => {
    event.stopPropagation();
    const url = `${window.location.origin}/five-slotlottery/${lotteryData.lotteryId}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      toast.success("Slot machine link copied!");
    }
  };

  const formatAmount = (amount) => {
    const value = Number(amount);
    if (Number.isNaN(value)) {
      return "0.00";
    }
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <Box
      className="slot-machine-card five-slot-card"
      sx={{
        cursor: "pointer",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "translateY(-4px)",
        },
        p: { xs: 2, sm: 2.5 },
      }}
      onClick={handleOpenLottery}
    >
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar
              sx={{
                width: 34,
                height: 34,
                bgcolor: colors.blue[100],
                color: colors.blue[700],
                fontWeight: 700,
              }}
            >
              5
            </Avatar>

            <Box>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    color: colors.slate[900],
                  }}
                >
                  {lotteryData.creator}
                </Typography>

                <VerifiedIcon
                  sx={{
                    fontSize: 14,
                    color: colors.primary.main,
                  }}
                />
              </Stack>

              <Typography
                variant="caption"
                sx={{
                  color: colors.slate[400],
                  fontFamily: "monospace",
                }}
              >
                Machine #{lotteryData.lotteryId}
              </Typography>
            </Box>
          </Stack>

          <Tooltip title="Share Slot Machine" arrow>
            <IconButton
              size="small"
              onClick={handleShare}
              sx={{
                color: colors.slate[400],
                p: 0.5,
              }}
            >
              <ShareIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* Mini, Major, Mega Jackpot 3-Column Bar */}
      <Box>
        <Box
          sx={{
            py: 1.5,
            borderTop: `1px solid ${colors.slate[100]}`,
            borderBottom: `1px solid ${colors.slate[100]}`,
            mb: 2.5,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box
              sx={{
                flex: 1,
                textAlign: "center",
                borderRight: `1px solid ${colors.slate[100]}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: colors.slate[500],
                  fontWeight: 600,
                  textTransform: "uppercase",
                  fontSize: "0.65rem",
                }}
              >
                Mini Jackpot
              </Typography>

              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  color: colors.blue[600],
                }}
              >
                {formatAmount(lotteryData.jackpots?.mini?.amount)}{" "}
                <span
                  style={{
                    fontSize: "0.7rem",
                    color: colors.blue[400],
                  }}
                >
                  {CURRENCY}
                </span>
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                textAlign: "center",
                borderRight: `1px solid ${colors.slate[100]}`,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: colors.slate[500],
                  fontWeight: 600,
                  textTransform: "uppercase",
                  fontSize: "0.65rem",
                }}
              >
                Major Jackpot
              </Typography>

              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  color: colors.blue[600],
                }}
              >
                {formatAmount(lotteryData.jackpots?.major?.amount)}{" "}
                <span
                  style={{
                    fontSize: "0.7rem",
                    color: colors.blue[400],
                  }}
                >
                  {CURRENCY}
                </span>
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                textAlign: "center",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: colors.slate[500],
                  fontWeight: 600,
                  textTransform: "uppercase",
                  fontSize: "0.65rem",
                }}
              >
                Mega Jackpot
              </Typography>

              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 800,
                  color: colors.blue[600],
                }}
              >
                {formatAmount(lotteryData.jackpots?.mega?.amount)}{" "}
                <span
                  style={{
                    fontSize: "0.7rem",
                    color: colors.blue[400],
                  }}
                >
                  {CURRENCY}
                </span>
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>

      {/* 5-Reels Slot Preview Stage */}
      <Box className="slot-machine-stage five-slot-stage">
        <Box className="slot-machine-top" sx={{ justifyContent: "center" }}>
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Typography
              variant="caption"
              sx={{
                color: colors.slate[500],
                fontWeight: 600,
                textTransform: "uppercase",
                mr: 1,
              }}
            >
              Lottery:
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: colors.slate[800],
              }}
            >
              {lotteryData.name}
            </Typography>
          </Stack>
        </Box>

        <Box className="slot-reels-wrapper five-slot-reels-wrapper">
          <Box className="slot-payline" />

          {reels.map((reel, index) => {
            const symbolIndex = SYMBOLS.findIndex(
              (symbol) => symbol.id === reel.id
            );

            const topSymbol =
              SYMBOLS[(symbolIndex + 1) % SYMBOLS.length];

            const bottomSymbol =
              SYMBOLS[(symbolIndex + SYMBOLS.length - 1) % SYMBOLS.length];

            return (
              <Box
                key={`${reel.id}-${index}`}
                className="slot-reel"
              >
                <Typography
                  className="slot-symbol slot-symbol-top"
                  sx={{
                    fontSize:
                      topSymbol.symbol === "BAR"
                        ? "0.95rem"
                        : "1.1rem",
                  }}
                >
                  {topSymbol.symbol}
                </Typography>

                <Typography
                  className="slot-symbol slot-symbol-main"
                  sx={{
                    fontSize:
                      reel.symbol === "BAR"
                        ? "1.1rem"
                        : "1.45rem",
                    fontWeight:
                      reel.symbol === "BAR"
                        ? 900
                        : undefined,
                  }}
                >
                  {reel.symbol}
                </Typography>

                <Typography
                  className="slot-symbol slot-symbol-bottom"
                  sx={{
                    fontSize:
                      bottomSymbol.symbol === "BAR"
                        ? "0.95rem"
                        : "1.1rem",
                  }}
                >
                  {bottomSymbol.symbol}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Action Button */}
      <Box sx={{ mt: 3 }}>
        <CommonButton
          variant="primary"
          size="small"
          startIcon={<OpenIcon />}
          onClick={(event) => {
            event.stopPropagation();
            handleOpenLottery();
          }}
          sx={{
            width: "100%",
            borderRadius: "10px",
            fontWeight: 800,
            height: "48px",
            bgcolor: colors.blue[600],
            "&:hover": {
              bgcolor: colors.blue[700],
            },
          }}
        >
          OPEN LOTTERY
        </CommonButton>
      </Box>
    </Box>
  );
};

export default FiveSlotMachineCard;